<template>
  <div class="page-content main-content wide">
    <div class="wrap">
      <div :class="['main-block', { 'with-sidebar': !isMobile }]">
        <div class="content">
          <h1>{{ pageData.title }}</h1>
          <div
            v-dompurify-html="pageData.description"
            class="description"
          />
        </div>

        <div class="form">
          <input-field
            :name="'firstName'"
            :field="'firstName'"
            :label="pageData.firstNameLabel + '*'"
            :value="formData.firstName"
            :error="$v.firstName.$error"
            :error-msg="getErrorMsg('firstName')"
            @input="setFirstName"
          />
          <input-field
            :name="'lastName'"
            :field="'lastName'"
            :label="pageData.lastNameLabel + '*'"
            :value="formData.lastName"
            :error="$v.lastName.$error"
            :error-msg="getErrorMsg('lastName')"
            @input="setLastName"
          />

          <input-field
            :name="'bookingRef'"
            :field="'bookingRef'"
            :label="$t('reusable.pnr.label')"
            :value="formData.bookingRef"
            :error="$v.bookingRef.$error"
            :error-msg="getErrorMsg('bookingRef')"
            :autocomplete="false"
            @input="setBookingRef"
          />

          <input-field
            :name="'paxCount'"
            :field="'paxCount'"
            :label="pageData.paxCountLabel + '*'"
            :value="formData.paxCount"
            :error="$v.paxCount.$error"
            :error-msg="getErrorMsg('paxCount')"
            @input="setPaxCount"
          />

          <input-field
            :name="'nationality'"
            :field="'nationality'"
            :label="pageData.nationalityLabel + '*'"
            :value="formData.nationality"
            :error="$v.nationality.$error"
            :error-msg="getErrorMsg('nationality')"
            @input="setNationality"
          />

          <input-field
            :name="'passNumber'"
            :field="'passNumber'"
            :label="pageData.passNumberLabel + '*'"
            :value="formData.passNumber"
            :error="$v.passNumber.$error"
            :error-msg="getErrorMsg('passNumber')"
            @input="setPassNumber"
          />

          <input-field
            :name="'passCountry'"
            :field="'passCountry'"
            :label="pageData.passCountryLabel + '*'"
            :value="formData.passCountry"
            :error="$v.passCountry.$error"
            :error-msg="getErrorMsg('passCountry')"
            @input="setPassCountry"
          />

          <label class="field-label">{{ pageData.whereFromLabel + '*' }}</label>
          <dropdown-white
            :class="['countries', {'error': $v.whereFrom.$error}]"
            :translation-prefix="'countries.'"
            :items="countries"
            :selected-item="''"
            :dropdown-class="'flag-'"
            :item-class="'flag-'"
            @select="selectCountry"
          />

          <input-field
            v-if="showCountryField"
            :name="'whereFrom'"
            :field="'whereFrom'"
            :label="pageData.whereFromOtherLabel"
            :value="formData.whereFrom"
            :error="$v.whereFrom.$error"
            :error-msg="getErrorMsg('whereFrom')"
            @input="setWhereFromCountry"
          />

          <input-field
            :name="'whereFromCity'"
            :field="'whereFromCity'"
            :label="pageData.whereFromLabelCity + '*'"
            :value="formData.whereFromCity"
            :error="$v.whereFromCity.$error"
            :error-msg="getErrorMsg('whereFromCity')"
            @input="setWhereFromCity"
          />

          <input-field
            :name="'email'"
            :field="'email'"
            :label="$t('reusable.email.label') + '*'"
            :value="formData.email"
            :error="$v.email.$error"
            :error-msg="getErrorMsg('firstName')"
            @input="setEmail"
          />

          <input-field
            :name="'phoneNumber'"
            :field="'phoneNumber'"
            :label="pageData.phoneLabel + '*'"
            :value="formData.phoneNumber"
            :error="$v.phoneNumber.$error"
            :error-msg="getErrorMsg('phoneNumber')"
            @input="setPhoneNumber"
          />

          <input-field
            :name="'comments'"
            :field="'comments'"
            :label="pageData.commentsLabel"
            :value="formData.comments"
            :error="$v.comments.$error"
            :error-msg="getErrorMsg('comments')"
            @input="setComments"
          />

          <checkbox-field
            :class="{ 'error': $v.resident.$error }"
            :checked="formData.resident"
            :name="'resident'"
            :label="pageData.residentLabel"
            @change="selectResident"
          />

          <checkbox-field
            :class="{ 'error': $v.family.$error }"
            :checked="formData.family"
            :name="'family'"
            :label="pageData.familyLabel"
            @change="selectFamily"
          />
        </div>

        <blue-button
          class="button"
          :disabled="formDisabled"
          :label="$t('buttons.confirm')"
          @clicked="submitForm()"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import isObject from 'lodash-es/isObject'
  import ScrollUtils from 'utils/scrollUtils'
  import FormMixin from 'components/Forms/FormMixin'
  import BlueButton from 'components/Buttons/Blue'
  import DefaultFormNotification from 'components/Notifications/DefaultFormNotification'

  export default {
    components: {
      'input-field': () => import(/* webpackChunkName: "input-field" */ 'components/Forms/Input'),
      'checkbox-field': () => import(/* webpackChunkName: "checkbox-field" */ 'components/Forms/Checkbox'),
      BlueButton,
      'dropdown-white': () => import(/* webpackChunkName: "dropdown-white" */ 'components/Dropdown/DropdownWhite'),
    },

    mixins: [
      FormMixin
    ],

    data () {
      return {
        showCountryField: false,
        formDisabled: false
      }
    },

    computed: {
      ...mapState('repatForm', [
        'pageData',
        'formData',
        'formErrors'
      ]),

      ...mapState('main/location', [
        'countries'
      ]),

      ...mapGetters('device', [
        'isMobile'
      ]),
      ...mapGetters('repatForm', [
        '$v'
      ])
    },

    methods: {
      getErrorMsg (name) {
        if (!this.$v || !this.formErrors) return ''

        const params = Object.keys(this.$v[`${name}`].$params)
        let error = ''
        params.forEach((param) => {
          if (this.$v[`${name}`][`${param}`] === false) {
            error = this.$t(this.formErrors[`${name}`][`${param}`])
          }
        })

        return error
      },
      setPaxCount (value) {
        this.$store.commit(`repatForm/setPaxCount`, value)
      },
      setNationality (value) {
        this.$store.commit(`repatForm/setNationality`, value)
      },
      setFirstName (value) {
        this.$store.commit(`repatForm/setFirstName`, value)
      },
      setLastName (value) {
        this.$store.commit(`repatForm/setLastName`, value)
      },
      setBookingRef (value) {
        this.$store.commit(`repatForm/setBookingRef`, value)
      },
      setEmail (value) {
        this.$store.commit(`repatForm/setEmail`, value)
      },
      setPhoneNumber (value) {
        this.$store.commit(`repatForm/setPhoneNumber`, value)
      },
      setPassNumber (value) {
        this.$store.commit(`repatForm/setPassNumber`, value)
      },
      setPassCountry (value) {
        this.$store.commit(`repatForm/setPassCountry`, value)
      },
      selectCountry (value) {
        if (value === 'ZZ') {
          this.$store.commit(`repatForm/setWhereFrom`, '')
        } else {
          this.$store.commit(`repatForm/setWhereFrom`, value)
        }
        this.showCountryField = value === 'ZZ'
      },
      setWhereFromCountry (value) {
        this.$store.commit(`repatForm/setWhereFrom`, value)
      },
      setWhereFromCity (value) {
        this.$store.commit(`repatForm/setWhereFromCity`, value)
      },
      setComments (value) {
        this.$store.commit(`repatForm/setComments`, value)
      },
      selectResident (value) {
        this.$store.commit(`repatForm/selectResident`, value)
      },
      selectFamily (value) {
        this.$store.commit(`repatForm/selectFamily`, value)
      },
      scrollToError (fieldGroup) {
        const position = fieldGroup.getBoundingClientRect().top
        const currentScroll = document.body.scrollTop || document.documentElement.scrollTop || 0
        const offset = -40
        ScrollUtils.smoothScroll(currentScroll + position + offset, 300)
      },
      submitForm () {
        this.formDisabled = true
        this.$store.commit('main/setShowSmallLoader', true)
        this.$store.dispatch('repatForm/submitForm')
          .then(() => {
            this.openNotification()
            this.$store.dispatch('repatForm/clearForm')
          })
          .catch((e) => {
            if (e !== false) {
              setTimeout(() => {
                this.showErrorModal(e)
              }, 200)
            } else {
              setTimeout(() => {
                const fieldGroup = document.querySelector('.error')
                if (fieldGroup) {
                  this.scrollToError(fieldGroup)
                } else {
                  this.showErrorModal(e)
                }
              }, 200)
            }
          })
          .finally(() => {
            this.$store.commit('main/setShowSmallLoader', false)
            this.formDisabled = false
          })
      },
      showErrorModal (err) {
        this.status = 'fail'

        const props = {
          status: this.status,
          failTitle: this.$t('notification.failTitle'),
          failMessage: !isObject(err) ? [err] : err,
          showCta: false
        }

        this.openModal(props)
      },
      openModal (props) {
        this.$store.dispatch('main/openModal')
        this.$store.commit('main/setShowModalHeaderWhite', false)
        this.$store.commit('main/setModalContent', DefaultFormNotification)
        this.$store.commit('main/setModalType', 'centered')
        this.$store.commit('main/setModalClass', 'modal-white-centered')
        this.$store.commit('main/setShowCloseIcon', true)
        this.$store.commit('main/setShowModalHeader', false)
        this.$store.commit('main/setModalCloseFn', this.closeNotificationModal)
        this.$store.commit('main/setModalProps', props)
      },

      closeNotificationModal () {
        this.$store.dispatch('main/closeModal')
      },
      openNotification () {
        this.status = 'success'
        this.$store.dispatch('main/openModal', { root: true })
        this.$store.commit('main/setModalContent', DefaultFormNotification, { root: true })
        this.$store.commit('main/setModalCloseFn', this.closeNotificationModal)
        this.$store.commit('main/setModalType', 'centered')
        this.$store.commit('main/setModalClass', 'modal-white-centered')
        this.$store.commit('main/setShowModalHeaderWhite', true)
        this.$store.commit('main/setModalTitle', '')
        this.$store.commit('main/setShowCloseIcon', true)
        this.$store.commit('main/setModalProps', {
          status: 'success',
          successTitle: this.pageData.successTitle,
          successMessage: this.pageData.successMessage,
          showCta: true
        })
      },
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .page-content {
    padding: 20px 0 20px;

    @include bp-tablet-only {
      padding-top: 37px;
    }

    @include bp-mf-desktop {
      padding: 40px 0 72px;
    }

    .wrap {
      display: flex;
      flex-direction: column;
      @include clearfix();

      @include bp-mf-desktop {
        display: block;
      }
    }
  }

  .main-block {
    flex-direction: column;
    order: 0;
    padding: 0 24px 28px;
    margin-top: 0;
    width: 100%;

    @include bp-mf-tablet {
      padding: 0 26px 26px;
      margin-top: -11px;
    }

    @include bp-mf-desktop {
      padding: 0 0 68px;
      flex-direction: row;
      justify-content: space-between;
      margin-top: -21px;
    }

    &.with-sidebar {
      @include bp-mf-tablet {
        margin-top: 0;
      }

      @include bp-desktop-small {
        position: relative;
        top: 0;
      }

      @include bp-mf-desktop {
        position: relative;
        vertical-align: top;


        .content {
          width: 100%;
        }
      }
    }
  }

  .field-label {
    opacity: .9;
    font-size: em(12);
    line-height: 14px;
    margin: 0 0 8px;
    display: block;
  }

  /deep/ .dropdown-wrap {

    &.countries {
      margin-bottom: 28px;

      @include bp-tablet {
        margin-bottom: 20px;
      }

      &.error {

        .dropdown-field {
          border: 1px solid $red-dark!important;
        }
      }

      .dropdown-field,
      .dropdown-list li a {
        padding-left: 57px;

        &:before {
          left: 16px;
          height: 18px;
        }
      }

      .dropdown-field {
        border: 1px solid $formInputBorder;
        font-size: em(16);
        line-height: 48px;
        min-height: 48px;

        &:before {
          top: 15px;
        }

        &:after {
          font-size: em(11);
          color: $gray-brown;
        }
      }

      &.active .dropdown-field:after {
        transform: rotate(270deg);
      }

      .dropdown-list {
        height: 192px;
        border: 1px solid $formInputBorder;
        border-top: none;
      }

      li {
        font-size: em(16);
        line-height: 48px;

        &.selected,
        &:hover {
          background-color: $gray-marble;

          a {
            color: $blue-medium;
          }
        }
      }

      a {
        color: $grey-default;

        &:before {
          top: 15px;
        }
      }
    }
  }
</style>
